import { Icon } from "@iconify/react";

const FOOTER_LINKS = [
    { label: "Preços", href: "#precos" },
    { label: "Prestadores", href: "#prestadores" },
    { label: "Módulos", href: "#modulos" },
    { label: "FAQ", href: "#faq" },
    { label: "Contactos", href: "#contacto" },
];

function scrollTo(href: string) {
    if (!href.startsWith("#")) {
        window.location.href = href;

        return;
    }

    const el = document.querySelector(href);
    if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
}

interface Props {
    /** Só há links de prestadores quando a secção dos prestadores está na página. */
    mostrarPrestadores?: boolean;
    /** As ligações de outra página do site (a dos prestadores), em vez das da página inicial. */
    links?: { label: string; href: string }[];
}

export default function WebsiteFooter({ mostrarPrestadores = false, links: ligacoes }: Props) {
    const links = ligacoes ?? FOOTER_LINKS.filter((link) => link.href !== "#prestadores" || mostrarPrestadores);

    return (
        <footer id="contacto" className="bg-slate-950 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
                    {/* Brand */}
                    <div className="sm:col-span-2 lg:col-span-1">
                        <div className="flex items-center gap-2.5 mb-4">
                            <img
                                src="/logi/imagens/logo.png"
                                alt="Logigate"
                                className="h-9 w-9"
                            />
                            <span className="text-xl font-bold">Logigate</span>
                        </div>
                        <p className="text-sm text-gray-400 leading-relaxed">
                            Software de gestão empresarial (ERP) completo e modular.
                            Um serviço da plataforma HongaYetu.
                        </p>
                    </div>

                    {/* Navigation */}
                    <div>
                        <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-gray-500">
                            Navegação
                        </h3>
                        <ul className="space-y-2.5">
                            {links.map((link) => (
                                <li key={link.href}>
                                    <button
                                        onClick={() => scrollTo(link.href)}
                                        className="text-sm text-gray-400 hover:text-white transition-colors"
                                    >
                                        {link.label}
                                    </button>
                                </li>
                            ))}
                            <li>
                                <a
                                    href="/criar"
                                    className="text-sm text-gray-400 hover:text-white transition-colors"
                                >
                                    Criar empresa
                                </a>
                            </li>
                            {mostrarPrestadores && (
                                <li>
                                    <a
                                        href="/criar?tipo=prestador"
                                        className="text-sm text-gray-400 hover:text-white transition-colors"
                                    >
                                        Criar conta de prestador
                                    </a>
                                </li>
                            )}
                            <li>
                                <a
                                    href="/empresas"
                                    className="text-sm text-gray-400 hover:text-white transition-colors"
                                >
                                    Entrar
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Legal */}
                    <div>
                        <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-gray-500">
                            Legal
                        </h3>
                        <ul className="space-y-2.5">
                            <li>
                                <a
                                    href={((window as any).urls?.main || "https://hongayetu.com") + "/legal/termos-logigate"}
                                    target="_blank"
                                    rel="noopener"
                                    className="text-sm text-gray-400 hover:text-white transition-colors"
                                >
                                    Termos de uso
                                </a>
                            </li>
                            <li>
                                <a
                                    href={((window as any).urls?.main || "https://hongayetu.com") + "/legal/privacidade-logigate"}
                                    target="_blank"
                                    rel="noopener"
                                    className="text-sm text-gray-400 hover:text-white transition-colors"
                                >
                                    Política de privacidade
                                </a>
                            </li>
                            <li>
                                <a
                                    href={((window as any).urls?.main || "https://hongayetu.com") + "/legal"}
                                    target="_blank"
                                    rel="noopener"
                                    className="text-sm text-gray-400 hover:text-white transition-colors"
                                >
                                    Documentos legais
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-gray-500">
                            Contactos
                        </h3>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-2.5">
                                <Icon icon="mdi:phone-outline" className="text-blue-500 shrink-0" width={18} />
                                <a
                                    href="tel:+244948242262"
                                    className="text-sm text-gray-400 hover:text-white transition-colors"
                                >
                                    +244 948 242 262
                                </a>
                            </li>
                            <li className="flex items-center gap-2.5">
                                <Icon icon="mdi:email-outline" className="text-blue-500 shrink-0" width={18} />
                                <a
                                    href="mailto:geral@hongayetu.com"
                                    className="text-sm text-gray-400 hover:text-white transition-colors"
                                >
                                    geral@hongayetu.com
                                </a>
                            </li>
                            <li className="flex items-start gap-2.5">
                                <Icon icon="mdi:map-marker-outline" className="text-blue-500 shrink-0 mt-0.5" width={18} />
                                <span className="text-sm text-gray-400">
                                    Rua Amilcar Cabral nº 66, Ingombota, Luanda, Angola
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-3">
                    <p className="text-sm text-gray-500">
                        &copy; {new Date().getFullYear()} Logigate. Todos os direitos reservados.
                    </p>
                    <p className="text-sm text-gray-500">
                        Um serviço da{" "}
                        <a
                            href={((window as any).urls?.main || "https://hongayetu.com")}
                            target="_blank"
                            rel="noopener"
                            className="text-blue-500 hover:text-blue-400 transition-colors"
                        >
                            HongaYetu
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
}
